/**
 * Records of things in a room go to the piece they refer to — never both.
 *
 * A split copies the room's SETTINGS onto the new room (storey, floor height, a
 * dorm's config — `inheritedFields` in `functions/features/room-split.js`).
 * That was also copying RECORDS OF THINGS IN THE ROOM, which then existed
 * twice: a removed scanned object listed in both rooms' `demolished` buckets is
 * demolished twice in the estimate. A record that refers to a place or to
 * objects goes to the piece it refers to (spec §5.4):
 *
 *   | field               | goes to                                              |
 *   | `demolished`        | each `objects` entry by its own position; BOTH get a  |
 *   |                     | bucket, even an empty one; other keys stay kept       |
 *   | `cabinetRuns`       | the piece the run's path midpoint is in               |
 *   | `cabinetLayout`     | kept, unless every object and wall it names left      |
 *   | `electrical`        | a hop to the piece holding BOTH its devices; a hop    |
 *   |                     | across the cut stays kept; other keys stay kept       |
 *   | `rakeEdges`         | by each edge's midpoint                               |
 *   | `sections`          | RoomPlan's labelled areas: by each `center`; one with |
 *   |                     | none stays kept                                       |
 *   | `ignoredCollisions` | a pair to the piece holding both objects; else gone   |
 *   | `roomIndex`,        | kept only                                             |
 *   | `centerClearance`   |                                                       |
 *
 * Pure. Returns partial room docs to spread over each side's doc.
 */

const { pieceOf } = require('./assign');
const { wallEndpoints } = require('./geometry');

const RECORD_FIELDS = [
  'demolished',
  'cabinetRuns',
  'cabinetLayout',
  'electrical',
  'rakeEdges',
  'ignoredCollisions',
  'roomIndex',
  'centerClearance',
  'sections',
];

const KEPT_ONLY = ['roomIndex', 'centerClearance'];

const mid = (a, b) => ({ x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 });
const pointOf = (el) => el?.center || el?.position || null;

/** The ends of one stored run segment, in world X-Z, or null. */
function segmentEnds(seg, wallsById) {
  if (seg?.freestanding) {
    return seg.start && seg.end ? { a: seg.start, b: seg.end } : null;
  }
  let frame = seg?.wallFrame;
  if (!frame) {
    const ends = wallEndpoints(wallsById.get(seg?.wallId));
    frame = ends ? { a: ends.bl, b: ends.br } : null;
  }
  if (!frame?.a || !frame?.b) return null;
  const len = Math.hypot(frame.b.x - frame.a.x, frame.b.z - frame.a.z);
  if (!(len > 0)) return null;
  const dir = {
    x: (frame.b.x - frame.a.x) / len,
    z: (frame.b.z - frame.a.z) / len,
  };
  const at = (t) => ({ x: frame.a.x + dir.x * t, z: frame.a.z + dir.z * t });
  const s0 = seg.startParam ?? 0;
  const s1 = seg.endParam ?? len;
  return { a: at(s0), b: at(s1) };
}

/** The point halfway along a run's whole path, or null. */
function runPathMidpoint(run, wallsById) {
  const legs = (run?.segments || [])
    .map((s) => segmentEnds(s, wallsById))
    .filter(Boolean)
    .map((e) => ({ ...e, len: Math.hypot(e.b.x - e.a.x, e.b.z - e.a.z) }));
  const total = legs.reduce((sum, l) => sum + l.len, 0);
  if (!(total > 0)) return legs[0] ? legs[0].a : null;
  let left = total / 2;
  for (const l of legs) {
    if (left <= l.len) {
      const t = l.len > 0 ? left / l.len : 0;
      return { x: l.a.x + (l.b.x - l.a.x) * t, z: l.a.z + (l.b.z - l.a.z) * t };
    }
    left -= l.len;
  }
  return legs[legs.length - 1].b;
}

/** Each list element by `sideOfEl` (1 | -1 | undefined → dropped). */
function partition(list, sideOfEl) {
  const out = { 1: [], '-1': [] };
  for (const el of list || []) {
    const side = sideOfEl(el);
    if (side === 1 || side === -1) out[side].push(el);
  }
  return out;
}

/**
 * @param {object} room the room being split
 * @param {object} cut a `cutRoomAlongChain` result
 * @param {1|-1} keptSide the piece that keeps the room's id
 * @returns {{ kept: object, created: object }} partial room docs
 */
function distributeRecords(room, cut, keptSide) {
  const createdSide = -keptSide;
  const side = (p) => pieceOf(p, cut, keptSide);
  const kept = {};
  const created = {};
  const put = (s, key, value) => {
    (s === keptSide ? kept : created)[key] = value;
  };

  // The side of every object, decided the way `assignByCenter` decides it.
  const objectSide = new Map();
  for (const o of room?.objects || []) {
    if (o?.id) objectSide.set(o.id, side(pointOf(o)));
  }
  const wallsById = new Map((room?.walls || []).map((w) => [w?.id, w]));

  // demolished — both pieces always carry a bucket.
  const bucket = room?.demolished || {};
  const removed = partition(bucket.objects, (o) => side(pointOf(o)));
  kept.demolished = { ...bucket, objects: removed[keptSide] };
  created.demolished = { objects: removed[createdSide] };

  if (Array.isArray(room?.cabinetRuns)) {
    const runs = partition(room.cabinetRuns, (r) =>
      side(runPathMidpoint(r, wallsById))
    );
    kept.cabinetRuns = runs[keptSide];
    created.cabinetRuns = runs[createdSide];
  }

  if (room?.cabinetLayout) {
    const layout = room.cabinetLayout;
    const sides = [
      ...(layout.appliances || [])
        .map((a) => objectSide.get(a?.id))
        .filter((s) => s !== undefined),
      ...(layout.cabinetWalls || [])
        .filter((cw) => cw?.kind === 'real' && wallsById.has(cw.id))
        .map((cw) => {
          const ends = wallEndpoints(wallsById.get(cw.id));
          return ends ? side(mid(ends.bl, ends.br)) : undefined;
        })
        .filter((s) => s !== undefined),
    ];
    const allLeft = sides.length > 0 && sides.every((s) => s === createdSide);
    put(allLeft ? createdSide : keptSide, 'cabinetLayout', layout);
  }

  if (room?.electrical) {
    const { connections, ...rest } = room.electrical;
    if (Array.isArray(connections)) {
      const hops = partition(connections, (c) => {
        const a = objectSide.get(c?.switchId);
        const b = objectSide.get(c?.fixtureId);
        return a === createdSide && b === createdSide ? createdSide : keptSide;
      });
      kept.electrical = { ...rest, connections: hops[keptSide] };
      created.electrical = { connections: hops[createdSide] };
    } else {
      kept.electrical = room.electrical;
    }
  }

  if (Array.isArray(room?.rakeEdges)) {
    const edges = partition(room.rakeEdges, (e) =>
      e?.from && e?.to ? side(mid(e.from, e.to)) : keptSide
    );
    kept.rakeEdges = edges[keptSide];
    created.rakeEdges = edges[createdSide];
  }

  if (Array.isArray(room?.ignoredCollisions)) {
    const pairs = partition(room.ignoredCollisions, (e) => {
      const a = objectSide.get(e?.a);
      const b = objectSide.get(e?.b);
      return a !== undefined && a === b ? a : undefined;
    });
    kept.ignoredCollisions = pairs[keptSide];
    created.ignoredCollisions = pairs[createdSide];
  }

  if (Array.isArray(room?.sections)) {
    const sections = partition(room.sections, (sec) => side(sec?.center));
    kept.sections = sections[keptSide];
    created.sections = sections[createdSide];
  }

  for (const key of KEPT_ONLY) {
    if (room && key in room) kept[key] = room[key];
  }

  return { kept, created };
}

module.exports = { RECORD_FIELDS, distributeRecords, runPathMidpoint };
